import { fetchToken } from '@agissc/ktag-map';
import { useMutation } from '@tanstack/react-query';

interface DownloadDocumentResponse {
  filename: string;
  blob: Blob;
  mimeType?: string;
}

const fetchDocument = async (url: string): Promise<DownloadDocumentResponse> => {
  const token = await fetchToken();
  const headers = new Headers();
  headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(response.status.toString() || response.statusText);
  }

  const blob = await response.blob();
  const mimeType =
    blob.type || response.headers.get('Content-Type') || 'application/octet-stream';
  const filename = url.split(/[\\/]/).pop() ?? 'download';

  return {
    filename,
    blob,
    mimeType,
  };
};

const downloadBlob = (blob: Blob, filename: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;

  // FF compatibility
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
};

const useDocumentDownload = () => {
  const mutation = useMutation({
    mutationFn: fetchDocument,
    onSuccess: (data) => {
      const blob = new Blob([data.blob], { type: data.mimeType });
      downloadBlob(blob, data.filename);
    },
  });

  return {
    downloadFile: (url: string) => mutation.mutate(url),
    isLoading: mutation.isPending,
    error: mutation.error as Error | null,
    isError: mutation.isError,
  };
};

export default useDocumentDownload;
